import React, { useEffect, useCallback, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import useUserStore from '../store/userStore';
import useSVGHandler from './useSVGHandler';

/**
 * Кнопка "Download SVG" с дизайном как у AddToCartButton.
 *
 * @param {Object[]}  assets
 * @param {boolean}   debug
 * @param {string}    archiveName
 * @param {boolean}   is_purchased
 * @param {boolean}   isSubscriptionActive
 * @param {function}  onBeforeAction   // колбэк: если вернёт (или зарезолвит) false, прерываем скачивание
 * @param {function}  onActionComplete // колбэк: вызывается после УСПЕШНОГО скачивания
 * @param {string}    className        // дополнительные классы для контейнера
 */
const DownloadSVGButton = ({
  assets,
  debug = false,
  archiveName,
  is_purchased = false,
  isSubscriptionActive = false,
  onBeforeAction,
  onActionComplete,
  className,
}) => {
  if (debug) {
    console.log('[DownloadSVGButton] props:', {
      assets,
      debug,
      archiveName,
      is_purchased,
      isSubscriptionActive,
    });
  }

  // Состояния
  const [isReady, setIsReady] = useState(false);
  const isUserAction = useRef(false);

  const accessToken = useUserStore((state) => state.accessToken);
  const isLoggedIn = useUserStore((state) => state.isLoggedIn);
  const refreshTokenIfNeeded = useUserStore((state) => state.refreshTokenIfNeeded);

  const {
    isDownloading,
    errorMessage,
    downloadSVGs,
    setErrorMessage,
  } = useSVGHandler(assets, accessToken, isLoggedIn, is_purchased, isSubscriptionActive, debug, archiveName);

  // Обновить токен перед использованием
  useEffect(() => {
    const updateToken = async () => {
      if (isLoggedIn) {
        await refreshTokenIfNeeded();
      }
    };
    
    updateToken();
  }, [isLoggedIn, refreshTokenIfNeeded]);

  // Предотвращаем начальный рендер до получения правильных данных
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  // Сбрасываем сообщение об ошибке через таймаут
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage, setErrorMessage]);

  /**
   * Функция, выполняющая непосредственно скачивание.
   */
  const doDownload = useCallback(async () => {
    if (isDownloading) return;
    
    // Устанавливаем флаг пользовательского действия
    isUserAction.current = true;
    
    try {
      if (debug) console.log('[DownloadSVGButton] Starting download…');
      
      // Запускаем скачивание
      await downloadSVGs();
      
      // Если скачивание успешно, вызываем onActionComplete
      if (onActionComplete) {
        onActionComplete();
      }

      if (debug) console.log('[DownloadSVGButton] Download done.');
      
    } catch (error) {
      if (debug) console.error('[DownloadSVGButton] Error downloading SVG:', error);
    }
  }, [downloadSVGs, onActionComplete, debug, isDownloading]);

  /**
   * Обработчик клика по кнопке.
   * 1) Проверяем onBeforeAction(). Если false — прерываем.
   * 2) Иначе запускаем doDownload().
   */
  const handleDownloadClick = async (e) => {
    e.preventDefault();
    if (isDownloading) return;

    // Обновляем токен перед скачиванием
    if (isLoggedIn) {
      await refreshTokenIfNeeded();
    }

    if (onBeforeAction) {
      const canContinue = await Promise.resolve(onBeforeAction());
      if (!canContinue) {
        return; // Прерываем
      }
    }

    doDownload();
  };

  // Не показываем ничего до завершения начальной настройки
  if (!isReady) {
    return (
      <div className={`relative min-h-[60px] overflow-hidden ${className || ''}`} 
           style={{ visibility: 'hidden' }}></div>
    );
  }

  return (
    <div className={`relative min-h-[60px] overflow-hidden ${className || ''}`}>
      {/* Единая кнопка с двумя состояниями текста */}
      <button
        className={`
          absolute w-full h-full text-base cursor-pointer border-none rounded-md text-white z-10 font-bold pl-5 bg-black
          ${isDownloading ? 'opacity-70 cursor-wait' : ''}
        `}
        onClick={handleDownloadClick}
        disabled={isDownloading}
        style={{ transform: 'none' }} /* Фиксируем позицию */
      >
        {isDownloading ? 'Downloading SVG...' : 'Download SVG'}
        
        {isDownloading && (
          <span className="ml-4 inline-block align-middle">
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 100 100" 
              preserveAspectRatio="xMidYMid" 
              className="align-middle"
            >
              <circle cx="50" cy="50" fill="none" stroke="#fff" strokeWidth="8" r="35" strokeDasharray="164.93361431346415 56.97787143782138">
                <animateTransform 
                  attributeName="transform" 
                  type="rotate" 
                  repeatCount="indefinite" 
                  dur="1s" 
                  values="0 50 50;360 50 50" 
                  keyTimes="0;1"
                />
              </circle>
            </svg>
          </span>
        )}
      </button>

      {/* Отображение ошибки */}
      {errorMessage && (
        <div className="absolute bottom-0 left-0 right-0 bg-red-500 text-white text-sm py-1 px-2 rounded-b-md">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

DownloadSVGButton.propTypes = {
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      collection: PropTypes.string.isRequired,
      media_type: PropTypes.string,
      is_free: PropTypes.bool,
      format: PropTypes.arrayOf(PropTypes.string).isRequired,
    })
  ).isRequired,
  debug: PropTypes.bool,
  archiveName: PropTypes.string,
  is_purchased: PropTypes.bool,
  isSubscriptionActive: PropTypes.bool,
  /** Если вернёт false, скачивание не происходит */
  onBeforeAction: PropTypes.func,
  /** Вызывается после УСПЕШНОГО скачивания (например, чтобы спрятать чекбокс) */
  onActionComplete: PropTypes.func,
  /** Дополнительные классы для контейнера */
  className: PropTypes.string,
};

DownloadSVGButton.defaultProps = {
  debug: false,
  archiveName: '',
  is_purchased: false,
  isSubscriptionActive: false,
  onBeforeAction: null,
  onActionComplete: null,
  className: '',
};

export default DownloadSVGButton; 